import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import React, { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';

import { getCharacterDetail } from '@api/chatbot';
import { fetchStory } from '@api/story';
import { fetchUser } from '@api/user';
import EFinderImage from '@components/common/EFinderImage';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IChatCharacter, IStoryDetail, IUserDetail } from '@model';
import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon } from 'chatie-design-system/icon';

import { formatNumberMillion } from '@utils/chat';
import {
  extractUniqueMentions,
  getMentionPath,
  MentionType,
  ParsedMention,
} from '@utils/mention';
import { useWebView } from '@utils/webview';

import Avatar, { AvatarSize } from '../common/Avatar';

const listCSS = css`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xsmall}px;
  margin-top: ${Spacing.small}px;

  &:empty {
    display: none;
    margin-top: 0;
  }
`;

const cardButtonCSS = (theme: Theme) => css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${Spacing.small}px;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.medium}px;
  border: 1px solid ${theme.var.gray100};
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    ${theme.var.gray50} 100%
  );
  text-align: left;

  &:active {
    background: ${theme.var.gray50};
  }

  .thumb {
    position: relative;
    width: 56px;
    height: 56px;
    overflow: hidden;
    flex-shrink: 0;
    border-radius: ${Radius.small}px;
    background: ${theme.var.gray100};
  }

  .meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    gap: 2px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 2px 6px;
    border-radius: 999px;
    background: ${theme.colors.main50};
    color: ${theme.colors.main500};
    ${Typography.body11};
    font-weight: 600;
  }

  .title {
    ${Typography.body14};
    font-weight: 700;
    color: ${theme.var.gray900};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description {
    ${Typography.body12};
    color: ${theme.var.gray600};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stats {
    ${Typography.body11};
    color: ${theme.var.gray500};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chevron {
    flex-shrink: 0;
  }
`;

const userThumbWrapCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const fallbackThumbCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.var.gray100};
  color: ${theme.var.gray400};
  ${Typography.body12};
`;

type MentionCardsProps = {
  content: string;
  className?: string;
};

type MentionCardItemProps = {
  mention: ParsedMention;
};

const getMentionEyebrow = (type: MentionType) => {
  switch (type) {
    case 'u':
      return '유저';
    case 's':
      return '작품';
    case 'c':
      return '캐릭터';
  }
};

const MentionCardItem = ({ mention }: MentionCardItemProps) => {
  const router = useRouter();
  const theme = useTheme();
  const { t } = useTranslation();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  const navigate = useCallback(
    (path: string) => {
      if (platform === Platform.web) {
        router.push(path);
      } else {
        web.openPage(path);
      }
    },
    [platform, router, web]
  );

  const userQuery = useQuery<IUserDetail | undefined>(
    QUERY_KEYS.USER.ONE(mention.id),
    () => fetchUser(mention.id),
    {
      enabled: mention.type === 'u',
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      staleTime: 60 * 1000,
    }
  );

  const storyQuery = useQuery<IStoryDetail>(
    QUERY_KEYS.STORY.ONE(mention.id),
    () => fetchStory(mention.id),
    {
      enabled: mention.type === 's',
      suspense: false,
      useErrorBoundary: false,
      retry: false,
      staleTime: 60 * 1000,
    }
  );

  const characterQuery = useQuery<IChatCharacter | undefined>(
    QUERY_KEYS.CHARACTER.ONE(mention.id),
    async () => {
      const data = await getCharacterDetail(mention.id);
      return data as IChatCharacter | undefined;
    },
    {
      enabled: mention.type === 'c',
      staleTime: 60 * 1000,
    }
  );

  const card = useMemo(() => {
    if (mention.type === 'u') {
      const user = userQuery.data;
      if (!user) {
        return null;
      }

      return {
        title: user.nickname,
        description: user.introduce || t('유저 프로필 보기'),
        stats: `${t('팔로워')} ${t('formatNumber', { val: user.followerCount || 0 })} · ${t('작품')} ${t('formatNumber', { val: user.storyCount || 0 })}`,
        thumb: (
          <div css={userThumbWrapCSS}>
            <Avatar size={AvatarSize.large} {...user} />
          </div>
        ),
      };
    }

    if (mention.type === 's') {
      const story = storyQuery.data;
      if (!story) {
        return null;
      }

      return {
        title: story.title,
        description: story.summary || story.user.nickname,
        stats: `${story.user.nickname} · ${t('탭')} ${t('formatNumber', { val: story.tapCount || 0 })}`,
        thumb: story.coverImage ? (
          <EFinderImage image={story.coverImage} alt={story.title} />
        ) : (
          <div css={fallbackThumbCSS(theme)}>{t('작품')}</div>
        ),
      };
    }

    const character = characterQuery.data as IChatCharacter | undefined;
    if (!character) {
      return null;
    }

    return {
      title: character.name,
      description: character.summary || t('캐릭터 프로필 보기'),
      stats: `${t('플레이')} ${formatNumberMillion(character.stat?.message_count || 0)} · ${t('좋아요')} ${t('formatNumber', { val: character.stat?.like_count || 0 })}`,
      thumb:
        character.profile_image || character.cover_image ? (
          <EFinderImage
            image={character.profile_image || character.cover_image}
            alt={character.name}
          />
        ) : (
          <div css={fallbackThumbCSS(theme)}>{t('캐릭터')}</div>
        ),
    };
  }, [characterQuery.data, mention.type, storyQuery.data, t, theme, userQuery.data]);

  if (!card) {
    return null;
  }

  return (
    <button
      type="button"
      css={cardButtonCSS(theme)}
      onClick={(e) => {
        e.stopPropagation();
        navigate(getMentionPath(mention));
      }}
    >
      <div className="thumb">{card.thumb}</div>
      <div className="meta">
        <span className="eyebrow">{getMentionEyebrow(mention.type)}</span>
        <strong className="title">{card.title}</strong>
        <span className="description">{card.description}</span>
        <span className="stats">{card.stats}</span>
      </div>
      <span className="chevron">
        <Icon
          icon={ArrowRight}
          size={Sizes.small}
          color={theme.var.gray400}
        />
      </span>
    </button>
  );
};

const MentionCards = ({ content, className }: MentionCardsProps) => {
  const mentions = useMemo(() => extractUniqueMentions(content), [content]);

  if (!mentions.length) {
    return null;
  }

  return (
    <div css={listCSS} className={className}>
      {mentions.map((mention) => (
        <MentionCardItem
          key={`${mention.type}:${mention.id}`}
          mention={mention}
        />
      ))}
    </div>
  );
};

export default MentionCards;
